import { Component, OnInit, ViewChild } from '@angular/core';
import { MassCommService, Message, MessageQueueSearch } from '../../shared/index';
import { Router } from '@angular/router';
import { Table as PrimeDataTable } from 'primeng/table';
import { NotificationService, PagedResultGeneric, UtilService } from '../../../shared/index';
import { TranslationService } from '../../../shared/translation/translation.service';
import { PagedResult } from '../../../shared/model/generic-paged-result.model';
import { DatagridService } from '../../../shared/index';
import { debounce } from 'lodash-decorators';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { MenuItem } from 'primeng/api';

/**
 * admin mass comm scheduled message
 */
@Component({
  selector: 'wl-mass-comm-message-admin-scheduled-messages',
  templateUrl: 'mass-comm-message-admin-scheduled-messages.component.html',
    standalone: false
})
export class MassCommMessageAdminScheduledMessagesComponent
  extends LazySelectableTableComponent<MessageQueueSearch, Message>
  implements OnInit
{
  @ViewChild('scheduledGrid', { static: false }) private _scheduledGrid: PrimeDataTable;

  public isBusy: boolean = false;
  public deleting: boolean = false;
  public isSearching: boolean = false;
  public scheduledResult: PagedResult<Array<Message>> = new PagedResult<Array<Message>>([]);
  private _messageToDelete: Message;
  public showDeleteConfirmationModal = false;
  public localTimeZoneAbbreviation: string;
  public rowMenuItems = new Map<number, any[]>();

  /**
   * inject
   * @param _massCommService
   */
  public constructor(
    private _massCommService: MassCommService,
    private _notificationService: NotificationService,
    private _translationService: TranslationService,
    private _router: Router,
    private _utilService: UtilService,
    public datagridService: DatagridService
  ) {
    super('MessageId', datagridService);
    this.searchParams = new MessageQueueSearch();
  }

  fetchResults(): Promise<PagedResultGeneric<Message>> {
    return this._massCommService.searchScheduled(this.searchParams);
  }

  @debounce(500)
  filterChanged() {
    this.searchParams.PageNumber = 1;
    this.getScheduledMessages();
  }

  /**
   * on init
   */
  public ngOnInit(): void {
    this.localTimeZoneAbbreviation = this._utilService.getLocalTimeZoneAbbreviation();
  }

  /**
   * start a search
   */
  public startSearch(): void {
    this._scheduledGrid.reset();
  }

  /**
   * for the ngprime data table
   */
  public lazyLoadScheduledMessages($event: any): void {
    this.datagridService.mapDatagridPagingToPagedSearch($event, this.searchParams);

    if (!this.isSearching) this.getScheduledMessages();
  }

  public getScheduledMessages() {
    if (this.isSearching) return;

    this.isSearching = true;
    this.searchParams.MessageSent = false;

    this.fetchResults().then(
      (result: PagedResult<Array<Message>>) => {
        this.datagridService.mapPagedResultsToPagedSearch(result, this.searchParams);
        this.scheduledResult = result;
        this.isSearching = false;
      },
      () => {
        this.isSearching = false;
      }
    );
  }

  /**
   * copy a given message
   */
  public copyMessage(message: Message): void {
    if (this.isBusy || !message) return;

    if (message.MessageId > 0) {
      this._router.navigate(['communications/message/copy', message.MessageId]);
    } else {
      this._router.navigate(['communications/message/draft/copy', message.DraftMessageId]);
    }
  }

  /**
   * edit a given message
   */
  public editMessage(message: Message): void {
    if (message.MessageId > 0) {
      this._router.navigate(['communications/message/scheduled', message.MessageId]);
    } else {
      this._router.navigate(['communications/message/draft', message.DraftMessageId]);
    }
  }

  /**
   * prompt before deleting
   */
  public confirmDeleteMessage(message: Message): void {
    if (this.deleting || (!message && !message.MessageId && !message.DraftMessageId)) {
      return;
    }

    this._messageToDelete = message;

    this.showDeleteConfirmationModal = true;
  }

  /**
   * delete a given message
   */
  public deleteMessageConfirmed(): void {
    if (this.deleting || !this._messageToDelete) return;

    this.deleting = true;

    const deleteOperation =
      this._messageToDelete.MessageId > 0
        ? this._massCommService.deleteMessage(this._messageToDelete.MessageId)
        : this._massCommService.deleteDraftMessage(this._messageToDelete.DraftMessageId);

    deleteOperation.then(
      () => {
        this.deleting = false;

        this._translationService
          .getTitlecase('communications.mass-comm-message-admin.message-deleted')
          .then((message: string) => this._notificationService.success(message));

        this.showDeleteConfirmationModal = false;
        this.getScheduledMessages();
      },
      () => {
        this.deleting = false;
      }
    );
  }
  actionItems(message: Message): MenuItem[] {
    const idKey = message.MessageId > 0 ? message.MessageId : message.DraftMessageId;
    if (!this.rowMenuItems.has(idKey)) {
      this.rowMenuItems.set(idKey, [
        {
          label: this._translationService.getInstantTitlecase('common.copy'),
          icon: 'glyphicon glyphicon-duplicate',
          command: () => {
            this.copyMessage(message);
          }
        },
        {
          label: this._translationService.getInstantTitlecase('common.edit'),
          icon: 'glyphicon glyphicon-edit',
          command: () => {
            this.editMessage(message);
          }
        },
        {
          label: this._translationService.getInstantTitlecase('common.delete'),
          icon: 'glyphicon glyphicon-trash',
          command: () => {
            this.confirmDeleteMessage(message);
          }
        }
      ]);
    }
    return this.rowMenuItems.get(idKey);
  }
}
